import all from './all';
import {useChangeStore} from '../../change';
import {useSelectionStore} from '../../selection';
import {GridEditor} from '../../../types';
import {
	breakItUp,
	getColumnsFromGrid,
	getColumnsByName,
	getDataFromGrid,
	getSortIndexesFromGrid,
} from '../../../utils';

import {FormValues} from '@netx/core-components/Form';

export default async function loose(
	gridEditor: GridEditor,
	selectionStore: ReturnType<typeof useSelectionStore>,
	changeStore: ReturnType<typeof useChangeStore>,
	initialValues: FormValues[],
	setProgress?: (progress: number) => void,
) {
	if (selectionStore.state.all)
		return all(gridEditor, changeStore, initialValues, setProgress);

	const {round} = Math;

	const data = getDataFromGrid(gridEditor);
	const columns = getColumnsFromGrid(gridEditor);
	const columnsByName = getColumnsByName(columns);
	const sortIndex = getSortIndexesFromGrid(gridEditor);

	const selectedRows = selectionStore.state.rows;
	const selectedRowCount = selectionStore.state.rowCount;

	const selectedColumnObjects = columns.filter((column) =>
		selectionStore.state.columns.includes(column.name),
	);
	const selectedColumnCount = selectedColumnObjects.length;

	const rowIndexesUpdating: number[] = selectedRows;
	const columnIndexesUpdating: number[] = selectedColumnObjects.map(
		(column) => column._index,
	);

	const changes = JSON.parse(JSON.stringify(changeStore.state.changes));

	let processCount = 3;
	if (!selectedRowCount) processCount--;
	if (!selectedColumnCount) processCount--;
	if (!selectionStore.state.loose) processCount--;

	if (selectedRowCount) {
		// First update rows
		const rowAmount = selectedRowCount <= 100 ? 25 : 100;
		await breakItUp(
			{
				amount: rowAmount,
				size: selectedRowCount,
			},
			(index) => {
				setProgress &&
					setProgress(
						round(((index + 1) / selectedRowCount / processCount) * 100),
					);

				const rowIndex = selectedRows[index];
				const realIndex = sortIndex[rowIndex];
				const rowData = data[realIndex];
				if (!rowData) return;

				const {assetId} = rowData;
				const changedFields = changes[assetId];
				if (!changedFields) return;

				const originalRowData = initialValues.find((d) => d.assetId == assetId);

				changedFields.forEach((field: string) => {
					const column = columnsByName[field];
					if (!column) return;
					rowData[field] = column.properties.parseInValue
						? column.properties.parseInValue(originalRowData?.[field])
						: originalRowData?.[field];
				});

				delete changes[assetId];
			},
		);
	}

	const changesToDelete: string[] = [];

	if (selectedColumnCount) {
		// Then get columns
		const dataSize = data.length;
		const amount = dataSize <= 100 ? 25 : 100;

		await breakItUp(
			{
				amount,
				size: dataSize,
			},
			(index) => {
				setProgress &&
					setProgress(round(((index + 1) / dataSize / processCount) * 100));

				const realIndex = sortIndex[index];
				const rowData = data[realIndex];

				if (!rowData || rowIndexesUpdating.includes(index)) return;

				const {assetId} = rowData;
				const originalRowData = initialValues.find(
					(d) => d.assetId === assetId,
				);
				if (!originalRowData) return;

				for (let x = 0, y = selectedColumnCount; x < y; x++) {
					const column = selectedColumnObjects[x];
					if (!column) continue;

					const {name, properties} = column;
					const changedFields = changes[assetId];
					const index = changedFields ? changedFields.indexOf(name) : -1;
					if (index < 0) continue;

					changedFields.splice(index, 1);
					!changedFields.length && changesToDelete.push(assetId.toString());

					rowData[name] = properties.parseInValue
						? properties.parseInValue(originalRowData?.[name])
						: originalRowData?.[name];
				}
			},
		);

		changesToDelete.forEach((assetId) => {
			delete changes[assetId];
		});
	}

	// Then points
	if (selectionStore.state.loose) {
		const points = await selectionStore.getPoints();
		const pointsSize = points.length;
		const pointAmount = pointsSize <= 100 ? 25 : 100;
		await breakItUp(
			{
				amount: pointAmount,
				size: pointsSize,
				increment: 2,
			},
			(index) => {
				setProgress &&
					setProgress(round(((index + 1) / pointsSize / processCount) * 100));

				const x = points[index];
				const y = points[index + 1];
				const realIndex = sortIndex[y];
				const rowData = data[realIndex];
				const column = columns[x];

				if (
					!rowData ||
					!column ||
					columnIndexesUpdating.includes(x) ||
					rowIndexesUpdating.includes(y)
				)
					return;

				const {assetId} = rowData;
				const {name, properties} = column;

				const changedFields = changes[assetId];
				if (!changedFields) return;

				const changeIndex = changedFields ? changedFields.indexOf(name) : -1;
				if (changeIndex < 0) return;

				const originalRowData = initialValues.find((d) => d.assetId == assetId);
				changedFields.splice(changeIndex, 1);
				if (!changedFields.length) delete changes[assetId];

				rowData[name] = properties.parseInValue
					? properties.parseInValue(originalRowData?.[name])
					: originalRowData?.[name];
			},
		);
	}

	changeStore.reset(changes);
	gridEditor.repaint();

	setProgress && setProgress(100);
}
